'use client'

import { FC, useEffect, useState } from 'react'

import { useRouter, useSearchParams } from 'next/navigation'
import { useForm } from 'react-hook-form'

import { TextField } from '@style-port/roov-ui-components-react'
import { StandardButton, ErrorTxt, Txt } from 'src/components/atoms'

import { ErrorStatuses } from 'src/constants'
import { yup, BaseYup, yupResolver } from 'src/customYup'
import { useError, useLoginFormSetting, useProfile } from 'src/hooks'
import { Routes } from 'src/routes'
import { CheckSignInSettingFormEntity } from 'src/types'

const schema: yup.SchemaOf<CheckSignInSettingFormEntity> = BaseYup.object({
  email: BaseYup.string().email('メールアドレスをご確認下さい').required(),
})

type Props = {
  className?: string
}

export const CheckSignInSetting: FC<Props> = ({ className }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [serverErrorMessage, setServerErrorMessage] = useState<string | undefined>(undefined)
  const { register, handleSubmit } = useForm<CheckSignInSettingFormEntity>({
    resolver: yupResolver(schema),
  })
  const router = useRouter()
  const searchParams = useSearchParams()

  const { loadLoginFormSetting, updateLoginFormSetting, enableLoginForm } = useLoginFormSetting()
  const { saveEmail } = useProfile()
  const { toErrorEntities } = useError()

  const onSubmit = async (formEntity: CheckSignInSettingFormEntity) => {
    setIsLoading(true)

    try {
      const loginFormSetting = await loadLoginFormSetting(formEntity.email)
      enableLoginForm()
      updateLoginFormSetting(loginFormSetting)

      // この後の認証時に必要になるため、メールアドレスを保存しておく
      await saveEmail({ email: formEntity.email, emailExpiresAt: null })

      router.push(`${Routes.SignInPassword.path}?${searchParams}`)
    } catch (error) {
      const errors = toErrorEntities(error)

      if (errors.some(err => err.statusCode === ErrorStatuses.Unauthorized)) {
        await router.push(Routes.IdPasswordSignIn.path)
        return
      }
      setServerErrorMessage('不明なエラーが発生しました')
      setIsLoading(false)
    }
  }

  useEffect(() => {
    return () => setIsLoading(false)
  }, [])

  return (
    <section className={className}>
      <div className="tw-bg-[rgba(255,255,255,0.6)] tw-rounded-[1.14rem] tw-p-r3">
        <Txt className="tw-font-bold tw-text-roovPrimary-default tw-text-center">ログイン</Txt>
        <form onSubmit={handleSubmit(onSubmit)} className="tw-mt-s1">
          <div className="tw-flex tw-flex-col tw-items-center">
            <TextField
              placeholder="メールアドレス"
              type="email"
              interactive
              {...register('email')}
              className="tw-w-full"
            />
            <div className="tw-w-full">
              {serverErrorMessage && (
                <ErrorTxt text={serverErrorMessage} showIcon className="tw-mt-s-2" />
              )}
            </div>

            <StandardButton
              size="l"
              text="次へ"
              isLoading={isLoading}
              prependIconPath="log-in/soft/line"
              className="tw-mt-s2"
              type="submit"
            />
          </div>
        </form>
      </div>
    </section>
  )
}
